import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserTier } from "@/hooks/useUserTier";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { validateCuiCnp } from "@/lib/cuiValidation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Search, Building2, User, Mail, CreditCard, AlertTriangle, Trash2, Download, ShieldCheck, Ban, Hand } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROMANIAN_COUNTIES, UNIT_CODES } from "@/lib/romanianCounties";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
import { useEffectiveBetaMode } from "@/hooks/useEffectiveBetaMode";
import DeletionBanner from "@/components/DeletionBanner";
import AccountantAutoSendCard from "@/components/AccountantAutoSendCard";
import AccountantSendHistory from "@/components/AccountantSendHistory";


const Settings = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { effectiveBetaMode: BETA_MODE } = useEffectiveBetaMode();
  const { tier: userTier, tierSlug } = useUserTier();
  const queryClient = useQueryClient();
  const [cuiLoading, setCuiLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [gdprSubmitting, setGdprSubmitting] = useState<null | "restrict" | "object">(null);

  const submitGdprRequest = async (type: "restrict" | "object") => {
    if (!user?.email) {
      toast.error("Trebuie să fii autentificat pentru a trimite cererea.");
      return;
    }
    setGdprSubmitting(type);
    try {
      const label = type === "restrict" ? "Restricționare" : "Opoziție";
      const subject =
        type === "restrict"
          ? "Cerere restricționare prelucrare date (GDPR)"
          : "Opoziție la prelucrarea datelor (GDPR)";
      const bodyText =
        type === "restrict"
          ? `Solicit restricționarea prelucrării datelor mele personale conform GDPR și Politicii de Confidențialitate §9.\n\nEmail cont: ${user.email}\nUser ID: ${user.id}`
          : `Îmi exercit dreptul de opoziție la prelucrarea datelor mele personale conform GDPR și Politicii de Confidențialitate §9.\n\nEmail cont: ${user.email}\nUser ID: ${user.id}`;

      // Routed through send-transactional-email so the request lands directly
      // in contact@ezfactura.ro (the operational inbox), tagged with a clear
      // [GDPR — …] prefix so it's instantly triageable in the mailbox.
      const fullMessage = `[GDPR — ${label}]\n\n${subject}\n\n${bodyText}`;
      const senderName = user.email.split("@")[0] || "User";
      const requestId = crypto.randomUUID();
      const submittedAt = new Date().toLocaleString("ro-RO", {
        dateStyle: "long",
        timeStyle: "short",
        timeZone: "Europe/Bucharest",
      });

      const { error: notifyErr } = await supabase.functions.invoke(
        "send-transactional-email",
        {
          body: {
            templateName: "contact-form-notification",
            recipientEmail: "contact@ezfactura.ro",
            idempotencyKey: `gdpr-${type}-${requestId}`,
            templateData: {
              senderName: `[GDPR-${label}] ${senderName}`,
              senderEmail: user.email,
              message: fullMessage,
              submittedAt,
            },
          },
        },
      );
      if (notifyErr) throw notifyErr;

      // Confirmation to the user — they expect an acknowledgement of a
      // legal request. Failure here is non-blocking.
      const { error: confirmErr } = await supabase.functions.invoke(
        "send-transactional-email",
        {
          body: {
            templateName: "contact-form-confirmation",
            recipientEmail: user.email,
            idempotencyKey: `gdpr-${type}-confirm-${requestId}`,
            templateData: { name: senderName, message: fullMessage },
          },
        },
      );
      if (confirmErr) {
        console.warn("GDPR confirmation send failed (non-blocking):", confirmErr);
      }

      toast.success(
        `Cererea ta de ${label.toLowerCase()} a fost trimisă. Răspundem în maxim 30 de zile.`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Nu am putut trimite cererea.";
      toast.error(`${msg} Încearcă din nou sau scrie-ne la contact@ezfactura.ro.`);
    } finally {
      setGdprSubmitting(null);
    }
  };

  const handleExportData = async () => {
    if (!user) return;
    setExporting(true);
    try {
      // Fetch profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      // Fetch all invoices
      const { data: invoices, error: invErr } = await supabase
        .from("invoices")
        .select("*")
        .order("created_at", { ascending: false });
      if (invErr) throw invErr;

      // Fetch all invoice items
      const invoiceIds = (invoices || []).map(i => i.id);
      let allItems: any[] = [];
      if (invoiceIds.length > 0) {
        // Batch in chunks of 50 to avoid URL length limits
        for (let i = 0; i < invoiceIds.length; i += 50) {
          const chunk = invoiceIds.slice(i, i + 50);
          const { data: items } = await supabase
            .from("invoice_items")
            .select("*")
            .in("invoice_id", chunk)
            .order("sort_order");
          if (items) allItems = [...allItems, ...items];
        }
      }

      // Build CSV content
      const csvParts: string[] = [];
      const sep = "────────────────────────────────────────";
      const exportDate = new Date().toISOString().split("T")[0];

      // Header
      csvParts.push("EZ Factura — Export Date Personale (GDPR)");
      csvParts.push(`Data export: ${exportDate}`);
      csvParts.push("");

      // Section 1: Profile
      csvParts.push(sep);
      csvParts.push("PROFIL");
      csvParts.push(sep);
      csvParts.push("Câmp,Valoare");
      if (profile) {
        csvParts.push(`Email,${user.email || ""}`);
        csvParts.push(`Nume afișat,"${profile.display_name || ""}"`);
        csvParts.push(`Firmă,"${profile.company_name || ""}"`);
        csvParts.push(`CUI,"${profile.cui || ""}"`);
        csvParts.push(`Adresă,"${profile.company_address || ""}"`);
        csvParts.push(`Oraș,"${profile.company_city || ""}"`);
        csvParts.push(`Județ,"${profile.company_county || ""}"`);
        csvParts.push(`Email contabil,"${profile.accountant_email || ""}"`);
        csvParts.push(`TVA implicit,${profile.default_vat_rate}%`);
        csvParts.push(`U.M. implicit,${profile.default_unit_code}`);
        csvParts.push(`Cont creat,${profile.created_at}`);
      }

      // Section 2: Invoices
      csvParts.push("");
      csvParts.push(sep);
      csvParts.push(`FACTURI (${(invoices || []).length} total)`);
      csvParts.push(sep);
      csvParts.push("Nr. Factură,Status,Data emiterii,Scadență,Furnizor,CUI Furnizor,Client,CUI Client,Subtotal,TVA,Total,Creat la");
      for (const inv of (invoices || [])) {
        csvParts.push([
          `"${inv.invoice_number || ""}"`,
          inv.status,
          inv.issue_date,
          inv.due_date || "",
          `"${inv.seller_name || ""}"`,
          `"${inv.seller_cui || ""}"`,
          `"${inv.buyer_name || ""}"`,
          `"${inv.buyer_cui || ""}"`,
          inv.subtotal,
          inv.vat_total,
          inv.total,
          inv.created_at,
        ].join(","));
      }

      // Section 3: Invoice Items
      csvParts.push("");
      csvParts.push(sep);
      csvParts.push(`PRODUSE / SERVICII (${allItems.length} total)`);
      csvParts.push(sep);
      csvParts.push("Nr. Factură,Denumire produs,Cantitate,U.M.,Preț unitar,TVA %,Total linie");
      for (const item of allItems) {
        const inv = (invoices || []).find(i => i.id === item.invoice_id);
        const lineTotal = Number(item.quantity) * Number(item.unit_price) * (1 + Number(item.vat_rate) / 100);
        csvParts.push([
          `"${inv?.invoice_number || ""}"`,
          `"${item.item_name}"`,
          item.quantity,
          item.unit_code,
          item.unit_price,
          item.vat_rate,
          lineTotal.toFixed(2),
        ].join(","));
      }

      // Footer
      csvParts.push("");
      csvParts.push(sep);
      csvParts.push("Sfârşit export. Acest fișier a fost generat automat de EZ Factura.");

      // Download CSV with BOM for Excel compatibility
      const bom = "\uFEFF";
      const blob = new Blob([bom + csvParts.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `export-ezfactura-${exportDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Export finalizat — ${(invoices || []).length} facturi și ${allItems.length} produse exportate.`);
    } catch (err: any) {
      toast.error(err.message || "Eroare la exportul datelor");
    } finally {
      setExporting(false);
    }
  };

  const isFreeTier = !userTier || tierSlug === "trial" || Number(userTier.price_ron) === 0;

  const [formInitialized, setFormInitialized] = useState(false);

  const [form, setForm] = useState({
    display_name: "",
    company_name: "",
    cui: "",
    company_address: "",
    company_city: "",
    company_county: "",
    accountant_email: "",
    default_vat_rate: "0",
    default_unit_code: "C62",
  });

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user!.id)
        .single();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  useEffect(() => {
    if (profile && !formInitialized) {
      setForm({
        display_name: profile.display_name || "",
        company_name: profile.company_name || "",
        cui: profile.cui || "",
        company_address: profile.company_address || "",
        company_city: profile.company_city || "",
        company_county: profile.company_county || "",
        accountant_email: profile.accountant_email || "",
        default_vat_rate: String(profile.default_vat_rate ?? 0),
        default_unit_code: profile.default_unit_code || "C62",
      });
      setFormInitialized(true);
    }
  }, [profile, formInitialized]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");

      if (profile) {
        const { error } = await supabase
          .from("profiles")
          .update({
            display_name: form.display_name || null,
            company_name: form.company_name || null,
            cui: form.cui || null,
            company_address: form.company_address || null,
            company_city: form.company_city || null,
            company_county: form.company_county || null,
            accountant_email: form.accountant_email || null,
            default_vat_rate: Number(form.default_vat_rate) || 0,
            default_unit_code: form.default_unit_code || "C62",
          })
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("profiles").insert({
          user_id: user.id,
          display_name: form.display_name || null,
          company_name: form.company_name || null,
          cui: form.cui || null,
          company_address: form.company_address || null,
          company_city: form.company_city || null,
          company_county: form.company_county || null,
          accountant_email: form.accountant_email || null,
          default_vat_rate: Number(form.default_vat_rate) || 0,
          default_unit_code: form.default_unit_code || "C62",
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      setFormInitialized(false);
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      toast.success("Profilul a fost salvat!");
    },
    onError: (err: any) => {
      toast.error(err.message || "Eroare la salvare");
    },
  });

  const FREE_TIER_ID = "5e299a3c-15c6-4eab-8267-00adc92c2282";

  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("profiles")
        .update({ subscription_tier_id: FREE_TIER_ID })
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-tier", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      setShowCancelConfirm(false);
      toast.success("Planul a fost anulat. Ești acum pe planul Gratuit.");
    },
    onError: (err: any) => {
      toast.error(err.message || "Eroare la anularea planului");
    },
  });

  const lookupCUI = async () => {
    const cui = form.cui.trim();
    if (!cui || cui.replace(/^RO/i, "").length < 2) return;

    setCuiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("cui-lookup", {
        body: { cui },
      });
      if (error) throw error;
      if (data?.error) {
        toast.error(data.error === "CUI not found" ? "CUI-ul nu a fost găsit" : data.error);
        return;
      }
      setForm((prev) => ({
        ...prev,
        company_name: data.name || prev.company_name,
        company_address: data.address || prev.company_address,
      }));
      toast.success("Date completate automat de la ANAF!");
    } catch {
      toast.error("Nu s-au putut prelua datele. Completează manual.");
    } finally {
      setCuiLoading(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <DeletionBanner />

      <main className="container py-6 max-w-xl space-y-8">
          {/* Personal */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              <h3 className="font-heading font-semibold">Informații Personale</h3>
            </div>
            <div className="space-y-2">
              <Label>Nume Afișat</Label>
              <Input
                placeholder="ex: Ion Popescu"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Input value={user?.email || ""} disabled className="opacity-60" />
                </TooltipTrigger>
                <TooltipContent>Emailul nu poate fi schimbat de aici</TooltipContent>
              </Tooltip>
              <p className="text-xs text-muted-foreground">Emailul nu poate fi schimbat de aici.</p>
            </div>
          </div>

          {/* Company */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <h3 className="font-heading font-semibold">Date Firmă</h3>
            </div>
            <div className="space-y-2">
              <Label>CUI / CNP</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="ex: 12345678, RO12345678, sau CNP"
                  value={form.cui}
                  onChange={(e) => setForm({ ...form, cui: e.target.value })}
                  onBlur={lookupCUI}
                  className={(() => {
                    const v = validateCuiCnp(form.cui);
                    return !v.valid ? "border-destructive focus-visible:ring-destructive" : "";
                  })()}
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={lookupCUI} disabled={cuiLoading}>
                      {cuiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Caută datele firmei după CUI</TooltipContent>
                </Tooltip>
              </div>
              {(() => {
                const v = validateCuiCnp(form.cui);
                if (!v.valid && v.error) return <p className="text-xs text-destructive">{v.error}</p>;
                if (v.valid && v.type === "cnp" && form.cui.trim()) return <p className="text-xs text-muted-foreground">Detectat: CNP (persoană fizică)</p>;
                if (v.valid && v.type === "cui" && form.cui.trim()) return <p className="text-xs text-muted-foreground">Detectat: CUI (firmă)</p>;
                return <p className="text-xs text-muted-foreground">Introdu CUI-ul și datele firmei se completează automat.</p>;
              })()}
            </div>
            <div className="space-y-2">
              <Label>Denumire Firmă</Label>
              <Input
                placeholder="Se completează automat"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Oraș</Label>
              <Input
                placeholder="ex: Cluj-Napoca"
                value={form.company_city}
                onChange={(e) => setForm({ ...form, company_city: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Județ</Label>
              <Select value={form.company_county} onValueChange={(v) => setForm({ ...form, company_county: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selectează județul" />
                </SelectTrigger>
                <SelectContent>
                  {ROMANIAN_COUNTIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Adresă Firmă</Label>
              <Input
                placeholder="Se completează automat"
                value={form.company_address}
                onChange={(e) => setForm({ ...form, company_address: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Cotă TVA implicită (%)</Label>
              <Select value={form.default_vat_rate} onValueChange={(v) => setForm({ ...form, default_vat_rate: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (neplatitor TVA / export)</SelectItem>
                  <SelectItem value="9">9% (locuințe)</SelectItem>
                  <SelectItem value="11">11% (cotă redusă)</SelectItem>
                  <SelectItem value="19">19% (cota standard anterioară)</SelectItem>
                  <SelectItem value="21">21% (standard)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Această valoare va fi folosită ca TVA implicit la crearea facturilor noi.</p>
            </div>
            <div className="space-y-2">
              <Label>Unitate de Măsură Implicită</Label>
              <Select value={form.default_unit_code} onValueChange={(v) => setForm({ ...form, default_unit_code: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_CODES.map((u) => (
                    <SelectItem key={u.code} value={u.code}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Această valoare va fi folosită ca U.M. implicit la crearea facturilor noi.</p>
            </div>
          </div>

          {/* Accountant */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              <h3 className="font-heading font-semibold">Contabil</h3>
            </div>
            <div className="space-y-2">
              <Label>Email Contabil</Label>
              <Input
                type="email"
                placeholder="contabil@exemplu.ro"
                value={form.accountant_email}
                onChange={(e) => setForm({ ...form, accountant_email: e.target.value })}
              />
              {profile?.accountant_email && (
                <p className="text-xs text-muted-foreground">
                  Contabil setat momentan:{" "}
                  <span className="font-semibold text-foreground">{profile.accountant_email}</span>
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Poți trimite manual facturile către contabil pe toate planurile.
              </p>
              <p className="text-xs text-muted-foreground">
                Exportul automat lunar (ZIP cu PDF-uri și XML-uri) este disponibil pe planul <span className="font-semibold text-primary">Profesional</span>.
              </p>
            </div>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="w-full h-12 font-semibold"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Salvează Setările
              </Button>
            </TooltipTrigger>
            <TooltipContent>Salvează toate modificările din profil</TooltipContent>
          </Tooltip>

          {/* Auto-send (Profesional) + manual consent management */}
          <AccountantAutoSendCard />

          {/* History of accountant sends */}
          <AccountantSendHistory />

          {/* Plan Management — only for paid users (cancel action).
              Free-tier display is removed: the active plan is already shown in the global header. */}
          {!isFreeTier && !BETA_MODE && (
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <h3 className="font-heading font-semibold">Planul Tău</h3>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{userTier?.name}</p>
                  <p className="text-sm text-muted-foreground">{userTier?.price_ron} RON/lună</p>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="sm" onClick={() => navigate("/select-plan")}>
                      Schimbă Planul
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Vezi și schimbă planul de abonament</TooltipContent>
                </Tooltip>
              </div>
              {!showCancelConfirm ? (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="text-sm text-muted-foreground hover:text-destructive transition-colors underline underline-offset-2"
                >
                  Anulează planul
                </button>
              ) : (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                    <p className="text-sm">
                      Ești sigur că vrei să anulezi planul <strong>{userTier?.name}</strong>? Vei fi retrogradat la planul <strong>Gratuit</strong> imediat.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => cancelMutation.mutate()}
                      disabled={cancelMutation.isPending}
                    >
                      {cancelMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                      Da, anulează
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCancelConfirm(false)}
                    >
                      Renunță
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Export Data (GDPR) */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Download className="h-5 w-5 text-primary" />
              <h3 className="font-heading font-semibold">Exportă Datele Mele</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Descarcă toate datele tale (profil, facturi, produse) într-un fișier CSV. Conform dreptului la portabilitate (GDPR).
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportData}
                  disabled={exporting}
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                  Exportă CSV
                </Button>
              </TooltipTrigger>
              <TooltipContent>Descarcă toate datele tale într-un fișier CSV</TooltipContent>
            </Tooltip>
          </div>

          {/* Data Retention Policy */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h3 className="font-heading font-semibold">Politica de Retenție a Datelor</h3>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                <p><strong className="text-foreground">Loguri tehnice:</strong> Logurile de email, token-urile de dezabonare utilizate și emailurile suprimate sunt păstrate maximum <strong className="text-foreground">12 luni</strong>, apoi sunt șterse automat.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                <p><strong className="text-foreground">Ștergere cont:</strong> După solicitarea ștergerii contului, datele tale (facturi, profil, setări) sunt păstrate <strong className="text-foreground">30 de zile</strong> pentru a permite anularea. După această perioadă, sunt șterse definitiv și irecuperabil.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                <p><strong className="text-foreground">Documente fiscale:</strong> Conform legislației, facturile pot fi arhivate până la <strong className="text-foreground">5 ani</strong> de la emitere.</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground pt-1">
              Pentru detalii complete, consultă <a href="/privacy" className="text-primary hover:underline">Politica de Confidențialitate</a>.
            </p>
          </div>

          {/* GDPR Rights — Restriction & Objection (manual via email — perfectly legal) */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h3 className="font-heading font-semibold">Drepturile tale GDPR — Restricționare &amp; Opoziție</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Ai dreptul, conform <a href="/privacy" className="text-primary hover:underline">Politicii de Confidențialitate (§9)</a> și GDPR, să ceri:
            </p>
            <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
              <li><strong className="text-foreground">Restricționarea prelucrării</strong> — „înghețarea" datelor tale (le păstrăm, dar nu le mai folosim activ)</li>
              <li><strong className="text-foreground">Opoziția la prelucrare</strong> — refuzi anumite prelucrări (ex: marketing, profilare)</li>
            </ul>
            <p className="text-xs text-muted-foreground">
              Aceste cereri sunt rare și fiecare necesită analiză individuală, deci se procesează manual prin email. Răspundem în maxim <strong>30 de zile</strong>, conform GDPR.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => submitGdprRequest("restrict")}
                    disabled={gdprSubmitting !== null}
                  >
                    {gdprSubmitting === "restrict" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Ban className="h-4 w-4 mr-2" />
                    )}
                    Cere restricționarea
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Trimite cererea direct prin aplicație. Vei primi răspuns pe email în maxim 30 de zile.</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => submitGdprRequest("object")}
                    disabled={gdprSubmitting !== null}
                  >
                    {gdprSubmitting === "object" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Hand className="h-4 w-4 mr-2" />
                    )}
                    Opune-te prelucrării
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Trimite cererea direct prin aplicație. Vei primi răspuns pe email în maxim 30 de zile.</TooltipContent>
              </Tooltip>
            </div>
            <p className="text-xs text-muted-foreground">
              Sau scrie-ne direct la <a href="mailto:contact@ezfactura.ro" className="text-primary hover:underline">contact@ezfactura.ro</a>.
            </p>
          </div>
          {!profile?.scheduled_deletion_at && (
          <div className="mt-8">
            <AlertDialog>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 font-semibold text-destructive border-destructive/30 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all duration-200 hover:scale-[1.02] active:scale-95"
                    >
                      <Trash2 className="h-5 w-5 mr-2" />
                      Șterge Contul
                    </Button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent>Șterge permanent contul și toate datele</TooltipContent>
              </Tooltip>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Șterge contul?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Contul tău va fi dezactivat imediat. Datele tale (facturi, profil, setări) vor fi șterse definitiv după 30 de zile. În această perioadă, ne poți contacta pentru a anula ștergerea.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Nu, păstrează contul</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={deleting}
                    onClick={async (e) => {
                      e.preventDefault();
                      setDeleting(true);
                      try {
                        const { data, error } = await supabase.functions.invoke("delete-account");
                        if (error) throw error;
                        if (data?.error) throw new Error(data.error);

                        // Notify the inbox so the team has a record of the
                        // deletion request (useful during the 30-day grace
                        // window in case the user calls in to cancel).
                        // Best-effort — never block the user's deletion flow.
                        if (user?.email) {
                          const submittedAt = new Date().toLocaleString("ro-RO", {
                            dateStyle: "long",
                            timeStyle: "short",
                            timeZone: "Europe/Bucharest",
                          });
                          supabase.functions
                            .invoke("send-transactional-email", {
                              body: {
                                templateName: "contact-form-notification",
                                recipientEmail: "contact@ezfactura.ro",
                                idempotencyKey: `account-deletion-${user.id}`,
                                templateData: {
                                  senderName: `[Cont-Ștergere programată] ${user.email}`,
                                  senderEmail: user.email,
                                  message:
                                    `Userul a solicitat ștergerea contului. Va fi șters definitiv în 30 de zile.\n\nUser ID: ${user.id}\nEmail: ${user.email}`,
                                  submittedAt,
                                },
                              },
                            })
                            .catch((err) =>
                              console.warn("Deletion notification failed (non-blocking):", err),
                            );
                        }

                        await supabase.auth.signOut();
                        toast.success("Contul tău va fi șters definitiv în 30 de zile. Dacă te răzgândești, contactează-ne.");
                        navigate("/");
                      } catch (err: any) {
                        toast.error(err.message || "Eroare la ștergerea contului");
                      } finally {
                        setDeleting(false);
                      }
                    }}
                  >
                    {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Da, șterge contul
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          )}
      </main>
      <AppFooter />
    </div>
  );
};

export default Settings;
